import React, { useState, useEffect } from "react";
import { X, CheckCircle, AlertTriangle, Clock, GitBranch, ArrowLeftRight, Lock, Shield, Star, FlaskConical, ChevronDown } from "lucide-react";
import client from "../../api/client";

// ── Status badge config ─────────────────────────────────────────────────────
const STATUS_CONFIG = {
  approved: {
    label: "Approved Baseline",
    icon: Shield,
    bg: "bg-emerald-900/60",
    border: "border-emerald-700",
    text: "text-emerald-300",
    dot: "bg-emerald-400",
  },
  authoritative: {
    label: "Authoritative",
    icon: Star,
    bg: "bg-emerald-900/60",
    border: "border-emerald-700",
    text: "text-emerald-300",
    dot: "bg-emerald-400",
  },
  superseded: {
    label: "Superseded",
    icon: AlertTriangle,
    bg: "bg-amber-900/40",
    border: "border-amber-700/50",
    text: "text-amber-400",
    dot: "bg-amber-400",
  },
  invalidated: {
    label: "QA / Invalidated",
    icon: FlaskConical,
    bg: "bg-slate-800/80",
    border: "border-slate-600/50",
    text: "text-slate-500",
    dot: "bg-slate-600",
  },
  draft: {
    label: "Draft",
    icon: Clock,
    bg: "bg-slate-800",
    border: "border-slate-600",
    text: "text-slate-400",
    dot: "bg-slate-500",
  },
};

function StatusBadge({ lockStatus, isReportBaseline, isCurrent }) {
  const cfg = STATUS_CONFIG[lockStatus] || STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
        <Icon size={9} />
        {cfg.label}
      </span>
      {isReportBaseline && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-950 border border-emerald-600 text-emerald-400">
          <Lock size={8} /> Report Baseline
        </span>
      )}
      {isCurrent && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-blue-950 border border-blue-600 text-blue-300">
          Active
        </span>
      )}
    </div>
  );
}

function fmt(val, decimals = 0) {
  if (val === null || val === undefined || val === 0) return "—";
  return Number(val).toLocaleString("en-US", { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
}

// ── Comparison Modal ────────────────────────────────────────────────────────
function ComparisonModal({ runA, runB, onClose }) {
  if (!runA || !runB) return null;

  const sA = runA.output_summary || {};
  const sB = runB.output_summary || {};

  const rows = [
    { label: "Methodology", a: runA.methodology || "—", b: runB.methodology || "—", unit: "" },
    { label: "Feedstock Methodology", a: sA.feedstock_methodology || "—", b: sB.feedstock_methodology || "—", unit: "" },
    { label: "MCF Factor", a: sA.mcf_factor != null ? sA.mcf_factor : "—", b: sB.mcf_factor != null ? sB.mcf_factor : "—", unit: "" },
    { label: "Capture Efficiency", a: sA.capture_efficiency_pct != null ? sA.capture_efficiency_pct + "%" : "—", b: sB.capture_efficiency_pct != null ? sB.capture_efficiency_pct + "%" : "—", unit: "" },
    { label: "Scope 1 Baseline", a: sA.scope1_baseline_tco2e_yr, b: sB.scope1_baseline_tco2e_yr, unit: "tCO₂e/yr", numeric: true },
    { label: "Net Scope 1 Reduction", a: sA.scope1_reduction_tco2e_yr, b: sB.scope1_reduction_tco2e_yr, unit: "tCO₂e/yr", numeric: true },
    { label: "Scope 2 Displaced", a: sA.scope2_displaced_tco2e_yr, b: sB.scope2_displaced_tco2e_yr, unit: "tCO₂e/yr", numeric: true },
    { label: "Conservative Total", a: sA.total_tco2e_yr, b: sB.total_tco2e_yr, unit: "tCO₂e/yr", numeric: true, key: true },
    { label: "Gross Avoidance (ref)", a: sA.gross_avoidance_tco2e_yr, b: sB.gross_avoidance_tco2e_yr, unit: "tCO₂e/yr", numeric: true },
    { label: "Electricity Output", a: sA.electricity_mwh_yr, b: sB.electricity_mwh_yr, unit: "MWh/yr", numeric: true },
    { label: "Has Calculation Trace", a: sA.has_calculation_trace ? "Yes" : "No", b: sB.has_calculation_trace ? "Yes" : "No", unit: "" },
    { label: "Status", a: runA.lock_status || "—", b: runB.lock_status || "—", unit: "" },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <ArrowLeftRight size={18} className="text-teal-400" />
            <div>
              <p className="font-bold text-white text-sm">Run Comparison</p>
              <p className="text-xs text-slate-400">Side-by-side methodology and reduction values</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        {/* Run headers */}
        <div className="grid grid-cols-3 px-6 py-3 border-b border-slate-700 bg-slate-800/50 flex-shrink-0">
          <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Parameter</div>
          <div>
            <p className="text-xs font-bold text-white truncate">{runA.version_label || `v${runA.version_number}`}</p>
            <StatusBadge lockStatus={runA.lock_status} isReportBaseline={runA.is_report_baseline} isCurrent={runA.is_current} />
          </div>
          <div>
            <p className="text-xs font-bold text-white truncate">{runB.version_label || `v${runB.version_number}`}</p>
            <StatusBadge lockStatus={runB.lock_status} isReportBaseline={runB.is_report_baseline} isCurrent={runB.is_current} />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          {rows.map((row, i) => {
            const aVal = row.numeric ? Number(row.a || 0) : row.a;
            const bVal = row.numeric ? Number(row.b || 0) : row.b;
            const diff = row.numeric && aVal && bVal ? bVal - aVal : null;
            const diffPct = row.numeric && aVal && aVal !== 0 ? ((bVal - aVal) / Math.abs(aVal)) * 100 : null;
            const changed = row.numeric ? Math.abs(diff) > 0.1 : (row.a !== row.b);
            return (
              <div key={row.label}
                className={`grid grid-cols-3 px-6 py-3 border-b border-slate-800 ${row.key ? "bg-slate-800/80" : ""} ${i % 2 === 0 ? "" : "bg-slate-900"}`}>
                <div className="flex items-center">
                  <span className={`text-xs ${row.key ? "font-bold text-white" : "text-slate-300"}`}>{row.label}</span>
                  {row.unit && <span className="text-[10px] text-slate-500 ml-1">{row.unit}</span>}
                </div>
                <div className={`text-xs font-mono ${row.lock_status === "superseded" ? "text-amber-400" : "text-slate-300"} ${changed ? "text-amber-400" : ""}`}>
                  {row.numeric ? fmt(aVal, 1) : row.a}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-mono font-bold ${row.key ? "text-emerald-400" : "text-white"}`}>
                    {row.numeric ? fmt(bVal, 1) : row.b}
                  </span>
                  {diff !== null && changed && (
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${diff > 0 ? "bg-red-900/50 text-red-400" : "bg-emerald-900/50 text-emerald-400"}`}>
                      {diff > 0 ? "+" : ""}{fmt(diff, 1)} ({diffPct > 0 ? "+" : ""}{diffPct?.toFixed(0)}%)
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="px-6 py-3 border-t border-slate-700 bg-slate-800/50 flex-shrink-0">
          <p className="text-[11px] text-slate-400">
            {(() => {
              const approvedRun = [runA, runB].find(r => r.is_report_baseline || r.lock_status === "approved");
              const supersededRun = [runA, runB].find(r => r.lock_status === "superseded");
              if (approvedRun && supersededRun) {
                return <>
                  <span className="font-semibold text-emerald-400">Approved Baseline:</span>{" "}
                  {approvedRun.version_label || `v${approvedRun.version_number}`} ({Number(approvedRun.output_summary?.total_tco2e_yr || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })} tCO₂e/yr).{" "}
                  <span className="text-amber-400">{supersededRun.version_label || `v${supersededRun.version_number}`}</span>{" superseded — "}
                  {supersededRun.lock_reason?.slice(0, 100) || "calculation correction"}...
                </>;
              }
              return `Comparing ${runA.version_label || `v${runA.version_number}`} vs ${runB.version_label || `v${runB.version_number}`}.`;
            })()}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Main Drawer ─────────────────────────────────────────────────────────────
export default function VersionHistoryDrawer({ projectId, isOpen, onClose }) {
  const [runs, setRuns]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [compareModal, setCompareModal] = useState(false);
  const [showQA, setShowQA]           = useState(false);  // hidden by default

  useEffect(() => {
    if (!isOpen || !projectId) return;
    setLoading(true);
    client.get(`/workspace/project/${projectId}/versions`)
      .then(r => setRuns(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen, projectId]);

  function toggleSelect(id) {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : prev.length < 2 ? [...prev, id] : [prev[1], id]
    );
  }

  // Default view: show only governance-tagged runs (approved, superseded) + current run
  // Hide invalidated/QA runs unless showQA toggle is on
  const visibleRuns = runs.filter(r => showQA || r.lock_status !== "invalidated");
  const qaRunCount  = runs.filter(r => r.lock_status === "invalidated").length;

  const selected = runs.filter(r => selectedIds.includes(r.id));
  const canCompare = selectedIds.length === 2;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full z-50 w-full max-w-2xl bg-slate-900 border-l border-slate-700 shadow-2xl flex flex-col"
        style={{ animation: "slideInRight 0.2s ease-out" }}>

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <GitBranch size={18} className="text-teal-400" />
            <div>
              <p className="font-bold text-white">Version History</p>
              <p className="text-xs text-slate-400">{visibleRuns.length} runs shown · Select 2 to compare</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canCompare && (
              <button
                data-testid="compare-runs-btn"
                onClick={() => setCompareModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-500 text-white text-xs font-bold rounded-lg transition-colors">
                <ArrowLeftRight size={12} />
                Compare Selected
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
              <X size={16} className="text-slate-400" />
            </button>
          </div>
        </div>

        {/* Governance legend */}
        <div className="px-6 py-2 border-b border-slate-800 flex items-center gap-3 flex-shrink-0 bg-slate-800/30">
          {Object.entries(STATUS_CONFIG).slice(0, 3).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${cfg.dot}`} />
              <span className="text-[10px] text-slate-400">{cfg.label}</span>
            </div>
          ))}
          {qaRunCount > 0 && (
            <button
              data-testid="qa-runs-toggle"
              onClick={() => setShowQA(v => !v)}
              className={`ml-auto flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded transition-colors
                ${showQA ? "bg-slate-700 text-slate-300" : "text-slate-500 hover:text-slate-400"}`}>
              <FlaskConical size={9} />
              {showQA ? "Hide" : "Show"} QA Runs ({qaRunCount})
              <ChevronDown size={9} className={`transition-transform ${showQA ? "rotate-180" : ""}`} />
            </button>
          )}
          {!qaRunCount && <span className="text-[10px] text-slate-500 ml-auto">Click rows to select for comparison</span>}
        </div>

        {/* Run list */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!loading && visibleRuns.map((run) => {
            const isSelected = selectedIds.includes(run.id);
            const isApproved = run.lock_status === "approved" || run.lock_status === "authoritative";
            const isSuperseded = run.lock_status === "superseded";
            const isInvalidated = run.lock_status === "invalidated";
            const summary = run.output_summary || {};

            return (
              <div
                key={run.id}
                data-testid={`version-row-${run.id}`}
                onClick={() => !isInvalidated && toggleSelect(run.id)}
                className={`px-6 py-4 border-b border-slate-800 transition-all
                  ${isInvalidated ? "opacity-40 cursor-default" : "cursor-pointer"}
                  ${isSelected ? "bg-teal-950/50 border-teal-800" : isInvalidated ? "" : "hover:bg-slate-800/60"}
                  ${run.is_report_baseline ? "bg-emerald-950/20" : ""}
                  ${isSuperseded ? "opacity-70" : ""}
                `}>
                <div className="flex items-start justify-between gap-3">
                  {/* Selection indicator — disabled for invalidated runs */}
                  <div className={`mt-0.5 w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center
                    ${isInvalidated ? "border-slate-700 bg-slate-800" :
                      isSelected ? "bg-teal-500 border-teal-400" : "border-slate-600"}`}>
                    {isSelected && <CheckCircle size={10} className="text-white" />}
                  </div>

                  {/* Main content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-xs font-bold text-white truncate">
                        {run.version_label || `v${run.version_number}`}
                      </span>
                      {run.is_locked && <Lock size={10} className="text-teal-400 flex-shrink-0" />}
                    </div>

                    <StatusBadge
                      lockStatus={run.lock_status}
                      isReportBaseline={run.is_report_baseline}
                      isCurrent={run.is_current} />

                    {/* Key metrics grid */}
                    <div className="grid grid-cols-3 gap-2 mt-2.5">
                      <div>
                        <p className="text-[9px] text-slate-500 uppercase tracking-wider">Conservative Total</p>
                        <p className={`text-xs font-bold ${isApproved ? "text-emerald-400" : isSuperseded ? "text-amber-400" : "text-slate-300"}`}>
                          {summary.total_tco2e_yr ? `${fmt(summary.total_tco2e_yr, 0)} tCO₂e/yr` : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[9px] text-slate-500 uppercase tracking-wider">Scope 2</p>
                        <p className="text-xs text-slate-300">{summary.scope2_displaced_tco2e_yr ? `${fmt(summary.scope2_displaced_tco2e_yr, 0)} tCO₂e/yr` : "—"}</p>
                      </div>
                      <div>
                        <p className="text-[9px] text-slate-500 uppercase tracking-wider">Electricity</p>
                        <p className="text-xs text-slate-300">{summary.electricity_mwh_yr ? `${fmt(summary.electricity_mwh_yr, 0)} MWh/yr` : "—"}</p>
                      </div>
                    </div>

                    {/* Metadata row */}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-[10px] text-slate-500 font-mono">{run.calculation_hash?.slice(0, 14)}...</span>
                      <span className="text-[10px] text-slate-500">{run.calculated_by}</span>
                      <span className="text-[10px] text-slate-500">{run.calculated_at?.slice(0, 10)}</span>
                    </div>

                    {/* Lock reason (truncated) */}
                    {run.lock_reason && (
                      <p className={`text-[10px] mt-1.5 leading-relaxed ${isSuperseded ? "text-amber-500/80" : "text-emerald-500/80"}`}>
                        {run.lock_reason.slice(0, 120)}{run.lock_reason.length > 120 ? "..." : ""}
                      </p>
                    )}
                  </div>

                  {/* Reduction hero */}
                  <div className="text-right flex-shrink-0">
                    <p className={`text-base font-black ${isApproved ? "text-emerald-400" : isSuperseded ? "text-amber-400/70" : "text-slate-400"}`}>
                      {summary.total_tco2e_yr ? fmt(summary.total_tco2e_yr, 0) : "—"}
                    </p>
                    <p className="text-[9px] text-slate-500">tCO₂e/yr</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-700 bg-slate-800/50 flex-shrink-0">
          <p className="text-[11px] text-slate-400">
            <span className="text-emerald-400 font-semibold">Locked runs</span> are protected — new calculations create draft runs without overwriting the approved baseline.
          </p>
        </div>
      </div>

      {/* Comparison modal */}
      {compareModal && canCompare && (
        <ComparisonModal
          runA={selected[0]}
          runB={selected[1]}
          onClose={() => setCompareModal(false)}
        />
      )}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}
